import {
  EnvironmentInjector,
  Injectable,
  Signal,
  computed,
  effect,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TitleDetailData } from '../navigation/nav.models';
import { routeChain } from '../navigation/route-snapshot';

const BRAND = 'B.Site';

/**
 * Titolo della finestra "<Pagina> · B.Site" (FR-027). Una rotta può aggiungere un dettaglio
 * che arriva dopo, come il nome del cantiere, con `data.titleDetail`: "Panoramica · Residenza
 * Via dei Tigli · B.Site".
 */
@Injectable({ providedIn: 'root' })
export class BsiteTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly injector = inject(EnvironmentInjector);
  private readonly page = signal<string | null>(null);
  private readonly detail = signal<Signal<string | null> | null>(null);

  /** Il titolo completo della pagina corrente, anche per gli annunci di cambio pagina. */
  readonly fullTitle = computed(() => {
    const parts = [this.page(), this.detail()?.() ?? null, BRAND].filter(
      (part): part is string => !!part,
    );
    return parts.join(' · ');
  });

  /** Il titolo della pagina senza il marchio, per gli annunci. */
  readonly pageTitle = computed(() =>
    [this.page(), this.detail()?.() ?? null].filter((part): part is string => !!part).join(' · '),
  );

  constructor() {
    super();
    effect(() => this.title.setTitle(this.fullTitle()));
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.page.set(this.buildTitle(snapshot) ?? null);
    let detail: TitleDetailData | null = null;
    let detailRoute = snapshot.root;
    for (const route of routeChain(snapshot.root)) {
      const declared = route.routeConfig?.data?.['titleDetail'] as TitleDetailData | undefined;
      if (declared) {
        detail = declared;
        detailRoute = route;
      }
    }
    const resolved = detail;
    this.detail.set(
      resolved ? runInInjectionContext(this.injector, () => resolved(detailRoute)) : null,
    );
    this.title.setTitle(this.fullTitle());
  }
}
